import React, { useState, useEffect, useCallback } from "react";
import { ColorTheme } from "../../../themes/colors";
import { screenshots } from "../../../data/screenshots";
import { useLanguage } from "../../../i18n/LanguageContext";

interface ScreenshotsGridProps {
    theme: ColorTheme;
    isMobile: boolean;
}

const floatingCardsMeta = [
    { icon: "💰", position: { top: "8%", left: "-80px" }, accent: false },
    { icon: "🤖", position: { top: "22%", right: "-90px" }, accent: true },
    { icon: "📊", position: { bottom: "28%", left: "-70px" }, accent: false },
    { icon: "🥗", position: { bottom: "14%", right: "-80px" }, accent: false },
];

const ScreenshotsGrid: React.FC<ScreenshotsGridProps> = ({ theme, isMobile }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [fading, setFading] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useLanguage();

    const goTo = useCallback((index: number) => {
        setFading(true);
        setTimeout(() => {
            setActiveIndex(index);
            setFading(false);
        }, 220);
    }, []);

    useEffect(() => {
        if (isHovered) return;
        const timer = setInterval(() => {
            goTo((activeIndex + 1) % screenshots.length);
        }, 3800);
        return () => clearInterval(timer);
    }, [activeIndex, isHovered, goTo]);

    const phoneW = isMobile ? 200 : 262;
    const phoneH = isMobile ? 418 : 548;
    const outerR = 46;
    const innerR = 38;

    const currentScreen = screenshots[activeIndex];

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: isMobile ? "auto" : "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                paddingTop: isMobile ? "1.5rem" : "0",
                paddingBottom: isMobile ? "3rem" : "0",
            }}
        >
            {/* ── Ambient glow behind phone ── */}
            <div
                style={{
                    position: "absolute",
                    width: `${phoneW + 80}px`,
                    height: `${phoneH * 0.7}px`,
                    background: `radial-gradient(ellipse at center, ${theme.primaryGlow} 0%, transparent 72%)`,
                    borderRadius: "50%",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />

            {/* ── Floating feature cards – desktop only ── */}
            {!isMobile && floatingCardsMeta.map((card, i) => (
                <div
                    key={i}
                    style={{
                        position: "absolute",
                        ...(card.position as React.CSSProperties),
                        background: card.accent
                            ? `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`
                            : "#FFFFFF",
                        color: card.accent ? "#FFFFFF" : theme.textPrimary,
                        borderRadius: "16px",
                        padding: "10px 14px",
                        boxShadow: card.accent
                            ? `0 8px 28px ${theme.primaryGlow}`
                            : "0 4px 20px rgba(0,0,0,0.10)",
                        border: card.accent ? "none" : `1px solid ${theme.cardBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        zIndex: 10,
                        backdropFilter: "blur(8px)",
                        animation: `float ${2.8 + i * 0.4}s ease-in-out infinite`,
                        animationDelay: `${i * 0.6}s`,
                        minWidth: "140px",
                    }}
                >
                    <span style={{ fontSize: "20px", lineHeight: 1 }}>{card.icon}</span>
                    <div>
                        <div style={{ fontSize: "12px", fontWeight: 700, lineHeight: 1.2 }}>{t.hero.floatingCards[i].title}</div>
                        <div style={{
                            fontSize: "10px",
                            lineHeight: 1.3,
                            opacity: 0.7,
                            color: card.accent ? "rgba(255,255,255,0.9)" : theme.textSecondary,
                        }}>{t.hero.floatingCards[i].subtitle}</div>
                    </div>
                </div>
            ))}

            {/* ── iPhone 17 Pro frame ── */}
            <div
                style={{
                    position: "relative",
                    width: `${phoneW}px`,
                    height: `${phoneH}px`,
                    zIndex: 5,
                    flexShrink: 0,
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Outer titanium frame */}
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: `${outerR}px`,
                        background: "linear-gradient(145deg, #4a4a52 0%, #2a2a30 40%, #1a1a20 100%)",
                        boxShadow: `
                            0 0 0 1px rgba(255,255,255,0.12),
                            0 32px 64px rgba(0,0,0,0.55),
                            0 16px 32px rgba(0,0,0,0.3),
                            inset 0 1px 0 rgba(255,255,255,0.18),
                            inset 0 -1px 0 rgba(0,0,0,0.3)
                        `,
                    }}
                />

                {/* Side buttons – right power */}
                <div style={{
                    position: "absolute",
                    right: "-3px",
                    top: "28%",
                    width: "3px",
                    height: "60px",
                    background: "linear-gradient(180deg, #555 0%, #444 100%)",
                    borderRadius: "0 2px 2px 0",
                    boxShadow: "1px 0 3px rgba(0,0,0,0.4)",
                }} />

                {/* Side buttons – left volume up */}
                <div style={{
                    position: "absolute",
                    left: "-3px",
                    top: "22%",
                    width: "3px",
                    height: "36px",
                    background: "linear-gradient(180deg, #555 0%, #444 100%)",
                    borderRadius: "2px 0 0 2px",
                    boxShadow: "-1px 0 3px rgba(0,0,0,0.4)",
                }} />
                {/* left volume down */}
                <div style={{
                    position: "absolute",
                    left: "-3px",
                    top: "33%",
                    width: "3px",
                    height: "36px",
                    background: "linear-gradient(180deg, #555 0%, #444 100%)",
                    borderRadius: "2px 0 0 2px",
                    boxShadow: "-1px 0 3px rgba(0,0,0,0.4)",
                }} />

                {/* Screen area */}
                <div
                    style={{
                        position: "absolute",
                        inset: "6px",
                        borderRadius: `${innerR}px`,
                        background: "#000",
                        overflow: "hidden",
                    }}
                >
                    {/* Screenshot image */}
                    <img
                        src={currentScreen.image}
                        alt={currentScreen.title}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "top center",
                            borderRadius: `${innerR}px`,
                            opacity: fading ? 0 : 1,
                            transition: "opacity 0.22s ease",
                            display: "block",
                        }}
                    />

                    {/* Bottom caption bar */}
                    <div style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: "28px 16px 16px",
                        background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)",
                        borderRadius: `0 0 ${innerR}px ${innerR}px`,
                        opacity: fading ? 0 : 1,
                        transition: "opacity 0.22s ease",
                    }}>
                        <div style={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: isMobile ? "11px" : "13px",
                            lineHeight: 1.3,
                            marginBottom: "2px",
                        }}>{t.screenshots[activeIndex].title}</div>
                        <div style={{
                            color: "rgba(255,255,255,0.72)",
                            fontSize: isMobile ? "9px" : "11px",
                            lineHeight: 1.3,
                        }}>{t.screenshots[activeIndex].subtitle}</div>
                    </div>

                    {/* Home indicator */}
                    <div style={{
                        position: "absolute",
                        bottom: "6px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "80px",
                        height: "3px",
                        background: "rgba(255,255,255,0.36)",
                        borderRadius: "2px",
                        zIndex: 25,
                    }} />
                </div>

                {/* Screen glare */}
                <div style={{
                    position: "absolute",
                    inset: "6px",
                    borderRadius: `${innerR}px`,
                    background: "linear-gradient(130deg, rgba(255,255,255,0.06) 0%, transparent 45%)",
                    pointerEvents: "none",
                    zIndex: 30,
                }} />
            </div>

            {/* ── Dot / thumbnail navigation ── */}
            <div style={{
                display: "flex",
                gap: "10px",
                marginTop: isMobile ? "20px" : "24px",
                alignItems: "center",
                justifyContent: "center",
            }}>
                {screenshots.map((s, i) => (
                    <button
                        key={i}
                        onClick={() => goTo(i)}
                        title={s.title}
                        style={{
                            width: i === activeIndex ? "28px" : "8px",
                            height: "8px",
                            borderRadius: "4px",
                            background: i === activeIndex
                                ? theme.primary
                                : theme.textLight,
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            transition: "all 0.3s ease",
                            outline: "none",
                            flexShrink: 0,
                        }}
                    />
                ))}
            </div>

            {/* ── Screen label ── */}
            <p style={{
                marginTop: "10px",
                fontSize: "12px",
                color: theme.textSecondary,
                textAlign: "center",
                fontWeight: 500,
                letterSpacing: "0.02em",
            }}>
                {activeIndex + 1} / {screenshots.length} — {t.screenshots[activeIndex].title}
            </p>
        </div>
    );
};

export default ScreenshotsGrid;
